'use client';

import {
  BriefcaseMedical,
  Building2,
  HeartPulse,
  Pill,
  Stethoscope,
  Store,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { UserType } from '../../schema/professional-profile.schema';
import type { AccountCategory } from './account-category-step';
import { WizardBackButton, WizardFooterBar } from './form-components';

interface UserTypeStepProps {
  category: AccountCategory;
  onSelect: (type: UserType) => void;
  onBack: () => void;
}

interface TypeOption {
  type: UserType;
  icon: React.ReactNode;
  labelKey: string;
  category: 'individual' | 'organization';
}

const typeOptions: TypeOption[] = [
  {
    type: 'doctor',
    icon: <Stethoscope className="h-6 w-6" strokeWidth={1} />,
    labelKey: 'profileWizard.doctor',
    category: 'individual',
  },
  {
    type: 'pharmacist',
    icon: <Pill className="h-6 w-6" strokeWidth={1} />,
    labelKey: 'profileWizard.pharmacist',
    category: 'individual',
  },
  {
    type: 'nurse_midwife',
    icon: <HeartPulse className="h-6 w-6" strokeWidth={1} />,
    labelKey: 'profileWizard.nurseMidwife',
    category: 'individual',
  },
  {
    type: 'hospital',
    icon: <Building2 className="h-6 w-6" strokeWidth={1} />,
    labelKey: 'profileWizard.hospital',
    category: 'organization',
  },
  {
    type: 'pharma',
    icon: <Store className="h-6 w-6" strokeWidth={1} />,
    labelKey: 'profileWizard.pharma',
    category: 'organization',
  },
  {
    type: 'medical_place',
    icon: <BriefcaseMedical className="h-6 w-6" strokeWidth={1} />,
    labelKey: 'profileWizard.medicalPlace',
    category: 'organization',
  },
];

export function UserTypeStep({
  category,
  onSelect,
  onBack,
}: UserTypeStepProps) {
  const { t } = useTranslation('account');

  const filteredOptions = typeOptions.filter((o) => o.category === category);

  const titleKey =
    category === 'organization'
      ? 'profileWizard.typeStepTitleOrganization'
      : 'profileWizard.typeStepTitleIndividual';

  const subtitleKey =
    category === 'organization'
      ? 'profileWizard.typeStepSubtitleOrganization'
      : 'profileWizard.typeStepSubtitleIndividual';

  return (
    <div className="space-y-6 pt-4">
      <div className="text-center">
        <h3 className="text-lg font-medium">{t(titleKey)}</h3>
        <p className="text-muted-foreground text-sm">{t(subtitleKey)}</p>
      </div>

      <div
        className={`grid gap-3 ${filteredOptions.length === 3 ? 'grid-cols-3' : 'grid-cols-2'}`}
      >
        {filteredOptions.map((option) => (
          <TypeCard
            key={option.type}
            option={option}
            onClick={() => onSelect(option.type)}
            t={t}
          />
        ))}
      </div>

      <WizardFooterBar className="justify-start">
        <WizardBackButton onClick={onBack} />
      </WizardFooterBar>
    </div>
  );
}

function TypeCard({
  option,
  onClick,
  t,
}: {
  option: TypeOption;
  onClick: () => void;
  t: (key: string) => string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="hover:border-primary hover:bg-muted/50 focus-visible:ring-ring flex flex-col items-center justify-center gap-2 rounded-lg border-2 p-4 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
    >
      <div className="text-muted-foreground">{option.icon}</div>
      <span className="text-sm font-medium">{t(option.labelKey)}</span>
    </button>
  );
}
